import { Component, Element, Host, h, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../../utils/debounce";
import { track } from "../../../utils/tracking";

/**
 * This component represents a detail group in the `<ux-iws-nb-all-in-one>` module.
 * It is used to group multiple detail items together, allowing users to switch between them.
 *
 * @slot (default) - Slot for the detail items (`<ux-iws-nb-all-in-one-detail>`)
 */
@Component({
    tag: "ux-iws-nb-all-in-one-detail-group",
    styleUrl: "nb-all-in-one-detail-group.scss",
    shadow: false,
})
export class NbAllInOneDetailGroup {
    private static uidIncrement = 0;
    private baseClass = "ux-iws-nb-all-in-one-detail-group";
    private uid = `nb-all-in-one-detail-group-${NbAllInOneDetailGroup.uidIncrement++}`;

    /** The element reference for the component. */
    @Element() host: HTMLUxIwsNbAllInOneDetailGroupElement;

    /**
     * Whether this group is currently active.
     */
    @Prop({ mutable: true, reflect: true }) active: boolean = false;

    /**
     * Unique group identifier used for linking tiles and groups.
     */
    @Prop({ reflect: true }) group: string = "";

    /**
     * Accessible label for the group (used for screen readers).
     */
    @Prop() label: string = "";

    /** tracking id that identifies in which detail group a tab was clicked */
    @Prop() trackingId: string = "";

    /**
     * Index of the currently active detail item in the group.
     */
    @State() activeDetailIndex: number = 0;

    /**
     * ID of the corresponding tab button for accessibility.
     */
    @State() tabButtonId: string | undefined = undefined;

    /**
     * Watches for changes to the 'active' property and manages group activation and animations.
     * @param active - Indicates if this group should be active.
     */
    @Watch("active")
    handleActiveChange(active: boolean) {
        if (!active) {
            this.host.style.height = `${this.host.scrollHeight}px`;
            this.slideOut();
            return;
        }
        const siblings =
            this.host.parentElement.querySelectorAll<HTMLUxIwsNbAllInOneDetailGroupElement>(
                "ux-iws-nb-all-in-one-detail-group"
            );
        siblings.forEach((el) => (el.active = el === this.host));
        const items = this.getDetailItems();
        const activeItem = items.find((item) => item.active) || items[0];
        if (!activeItem) {
            return;
        }
        items.forEach((el) => (el.active = el === activeItem));
        this.host.style.height = `0px`;
        this.slideIn();
    }

    /**
     * Watches for changes to the active detail index and updates which detail is active.
     * @param activeIndex - The new active detail index.
     */
    @Watch("activeDetailIndex")
    handleActiveDetailIndexChange(activeIndex: number) {
        this.getDetailItems().forEach((item, index) => {
            item.active = index === activeIndex;
        });
    }

    /**
     * Returns the related tile element for this group, if present.
     * @returns The related tile element or null if not found.
     */
    private getRelatedTile = (): HTMLUxIwsNbAllInOneTileElement | null => {
        return this.host.parentElement.querySelector<HTMLUxIwsNbAllInOneTileElement>(
            `ux-iws-nb-all-in-one-tile[group="${this.group}"]`
        );
    };

    /**
     * Animates the group sliding in when activated.
     */
    private slideIn = debounce(() => {
        if (!this.active) {
            return;
        }
        this.host.style.height = `${this.host.scrollHeight}px`;
        this.slideInDone();
    }, 50);

    /**
     * Completes the slide-in animation and scrolls the group into view.
     */
    private slideInDone = debounce(() => {
        if (!this.active) {
            return;
        }
        this.host.style.height = "auto";
        const scrollTarget = this.getRelatedTile() || this.host;
        scrollTarget.scrollIntoView({
            behavior: "smooth",
        });
    }, 300);

    /**
     * Animates the group sliding out when deactivated.
     */
    private slideOut = debounce(() => {
        if (this.active) {
            return;
        }
        this.host.style.height = `0px`;
    }, 50);

    /**
     * Returns all detail items within this group.
     * @returns An array of detail item elements.
     */
    private getDetailItems = (): HTMLUxIwsNbAllInOneDetailElement[] => {
        return Array.from(
            this.host.querySelectorAll<HTMLUxIwsNbAllInOneDetailElement>(
                "ux-iws-nb-all-in-one-detail"
            )
        );
    };

    /**
     * Handles click events on tab buttons to switch the active detail item.
     * @param event - The click event from the tab button.
     */
    private handleClickTabButton = (event: Event) => {
        const target = event.target as HTMLElement;
        const button: HTMLUxButtonElement =
            target.closest<HTMLUxButtonElement>("ux-button");
        const activeIndex = Array.from(
            button.parentElement.querySelectorAll("ux-button") || []
        ).indexOf(button);
        if (Number.isNaN(activeIndex)) {
            return;
        }
        this.activeDetailIndex = activeIndex;
    };

    /**
     * Handles changes to the select dropdown to switch the active detail item.
     * @param event - The custom event from the select dropdown.
     */
    private handleSelectChange = (event: CustomEvent) => {
        const index = parseInt(event.detail.value);
        if (Number.isNaN(index)) {
            return;
        }
        this.activeDetailIndex = index;
    };

    /**
     * Renders a tab button for a detail item.
     * @param item - The detail item element.
     * @param index - The index of the detail item.
     * @param activeIndex - The index of the currently active detail item.
     * @returns The rendered button element.
     */
    private renderButton = (
        item: HTMLUxIwsNbAllInOneDetailElement,
        index: number,
        activeIndex: number
    ) => {
        return (
            <ux-button
                key={`${index}-${item.label}`}
                class="ux-iws-nb-all-in-one-detail-group__button"
                label={item.label}
                selected={activeIndex === index}
                onClick={(e) => {
                    track("click_all_in_one_detail_tab", {
                        tab: item.getAttribute("tracking-id"),
                        group: this.trackingId,
                    });
                    this.handleClickTabButton(e);
                }}
                role="tab"
                aria-selected={activeIndex === index ? "true" : "false"}
                aria-controls={item.id}
            ></ux-button>
        );
    };

    componentDidLoad() {
        window.setTimeout(() => {
            const tile = this.getRelatedTile();
            if (tile && tile.id) {
                this.tabButtonId = tile.id;
            }
        }, 0);
    }

    render() {
        const { baseClass } = this;
        const items = this.getDetailItems();
        const activeItem = items.find((item) => item.active) || items[0];
        const activeIndex = items.indexOf(activeItem);
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--active`]: this.active,
                }}
                role="tabpanel"
                id={this.uid}
                aria-label={this.label}
                aria-labelledby={this.label ? undefined : this.tabButtonId}
                aria-hidden={this.active ? "false" : "true"}
                tabindex={-1}
                inert={!this.active}
            >
                <div class={`${baseClass}__inner`}>
                    {items.length > 1 && (
                        <div class={`${baseClass}__navigation`}>
                            <div class={`${baseClass}__buttons`} role="tablist">
                                {items.map((item, index) =>
                                    this.renderButton(item, index, activeIndex)
                                )}
                            </div>
                            <ux-select
                                class={`${baseClass}__dropdown`}
                                onUxChange={this.handleSelectChange}
                            >
                                {items.map((item, index) => (
                                    <option
                                        key={`${index}-${item.label}`}
                                        value={`${index}`}
                                        selected={index === activeIndex}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </ux-select>
                        </div>
                    )}
                    <div class={`${baseClass}__items`}>
                        <slot />
                    </div>
                </div>
            </Host>
        );
    }
}
